import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from '../../modules/Login/AuthContext';
import { useNavigate } from 'react-router-dom';
import SessionLockModal from './SessionLockModal';

// Tiempo total de inactividad: 15 minutos (900 segundos)
const SESSION_TIMEOUT_SECONDS = 15 * 60;
// Aviso cuando falten 2 minutos (120 segundos)
const WARNING_THRESHOLD_SECONDS = 2 * 60;

export default function SessionGuard() {
  const { isAuthenticated, logout, lockSession, unlockSession } = useAuth();
  const navigate = useNavigate();

  const [currentTime, setCurrentTime] = useState('');

  // 🔒 1. Inicializar estado de bloqueo persistido en sessionStorage (Resistente a F5 / Recarga - ISO 27001)
  const [isLocked, setIsLocked] = useState(() => {
    if (!sessionStorage.getItem('isAuthenticated') || sessionStorage.getItem('isAuthenticated') !== 'true') {
      return false;
    }
    if (sessionStorage.getItem('isSessionLocked') === 'true') {
      return true;
    }
    const savedLastActivity = parseInt(sessionStorage.getItem('sessionLastActivity') || '0', 10);
    if (savedLastActivity > 0) {
      const elapsed = Math.floor((Date.now() - savedLastActivity) / 1000);
      if (elapsed >= SESSION_TIMEOUT_SECONDS) {
        sessionStorage.setItem('isSessionLocked', 'true');
        return true;
      }
    }
    return false;
  });

  const [secondsRemaining, setSecondsRemaining] = useState(() => {
    if (sessionStorage.getItem('isSessionLocked') === 'true') {
      return 0;
    }
    const savedLastActivity = parseInt(sessionStorage.getItem('sessionLastActivity') || '0', 10);
    if (savedLastActivity > 0) {
      const elapsed = Math.floor((Date.now() - savedLastActivity) / 1000);
      return Math.max(0, SESSION_TIMEOUT_SECONDS - elapsed);
    }
    return SESSION_TIMEOUT_SECONDS;
  });

  const [warningOpen, setWarningOpen] = useState(false);

  const lastActivityRef = useRef(
    parseInt(sessionStorage.getItem('sessionLastActivity') || '0', 10) || Date.now()
  );
  const timerIntervalRef = useRef(null);
  const clockIntervalRef = useRef(null);

  // 🕒 1. Actualizar Reloj en Tiempo Real
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      setCurrentTime(timeStr);
    };

    updateClock();
    clockIntervalRef.current = setInterval(updateClock, 1000);

    return () => {
      if (clockIntervalRef.current) clearInterval(clockIntervalRef.current);
    };
  }, []);

  // 🔄 2. Resetear el temporizador al detectar actividad
  const resetTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    sessionStorage.setItem('sessionLastActivity', now.toString());
    setSecondsRemaining(SESSION_TIMEOUT_SECONDS);
    if (warningOpen) {
      setWarningOpen(false);
    }
  }, [warningOpen]);

  // Si recién carga y no tenía sessionLastActivity guardado, guardarlo ahora
  useEffect(() => {
    if (isAuthenticated && !sessionStorage.getItem('sessionLastActivity')) {
      sessionStorage.setItem('sessionLastActivity', Date.now().toString());
    }
  }, [isAuthenticated]);

  // 👂 3. Listeners de eventos de actividad del usuario (Pausados si está bloqueado)
  useEffect(() => {
    if (!isAuthenticated || isLocked) return;

    let throttleTimeout = null;
    const handleUserActivity = () => {
      if (isLocked) return;
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 2000); // Throttle de 2 seg para máximo rendimiento
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((evt) => window.addEventListener(evt, handleUserActivity, { passive: true }));

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, handleUserActivity));
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [isAuthenticated, isLocked, resetTimer]);

  // ⏳ 4. Cuenta regresiva de sesión
  useEffect(() => {
    if (!isAuthenticated || isLocked) return;

    timerIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, SESSION_TIMEOUT_SECONDS - elapsed);
      setSecondsRemaining(remaining);

      // Abrir aviso si quedan menos de 2 minutos
      if (remaining <= WARNING_THRESHOLD_SECONDS && remaining > 0) {
        setWarningOpen(true);
      }

      // Si llega a 0 -> Bloquear pantalla conservando estado y datos (ISO 27001)
      if (remaining === 0) {
        clearInterval(timerIntervalRef.current);
        setWarningOpen(false);
        sessionStorage.setItem('isSessionLocked', 'true');
        setIsLocked(true);
        if (lockSession) lockSession();
      }
    }, 1000);

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isAuthenticated, isLocked, lockSession]);

  const handleUnlock = async (password) => {
    if (unlockSession) {
      await unlockSession(password);
    }
    sessionStorage.removeItem('isSessionLocked');
    const now = Date.now();
    sessionStorage.setItem('sessionLastActivity', now.toString());
    lastActivityRef.current = now;
    setSecondsRemaining(SESSION_TIMEOUT_SECONDS);
    setIsLocked(false);
  };

  const handleForceLogout = () => {
    sessionStorage.removeItem('isSessionLocked');
    sessionStorage.removeItem('sessionLastActivity');
    setIsLocked(false);
    logout();
    navigate('/', { replace: true });
  };

  // Formato mm:ss
  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isLowTime = secondsRemaining <= WARNING_THRESHOLD_SECONDS;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          userSelect: 'none',
        }}
      >
        {/* Contador de Inactividad con Hora en Tooltip */}
        <Tooltip
          title={
            <Box sx={{ p: 0.2, textAlign: 'center' }}>
              <Box sx={{ fontWeight: 700, mb: 0.2 }}>⏱️ Tiempo de sesión: {formatCountdown(secondsRemaining)}</Box>
              <Box sx={{ fontSize: '0.7rem', opacity: 0.85 }}>🕒 Hora del sistema: {currentTime || '--:--'}</Box>
            </Box>
          }
          arrow
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              px: 0.6,
              py: 0.15,
              height: 23,
              borderRadius: '5px',
              bgcolor: isLowTime ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${isLowTime ? '#fca5a5' : '#bbf7d0'}`,
              color: isLowTime ? '#dc2626' : '#15803d',
              fontSize: '0.69rem',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              cursor: 'default',
              transition: 'all 0.2s ease',
            }}
          >
            <TimerIcon
              sx={{
                fontSize: '13px',
                color: isLowTime ? '#dc2626' : '#16a34a',
                animation: isLowTime ? 'pulse 1s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                  '100%': { opacity: 1 },
                }
              }}
            />
            <span>{formatCountdown(secondsRemaining)}</span>
          </Box>
        </Tooltip>
      </Box>

      {/* Modal de Advertencia de Inactividad */}
      <Dialog
        open={warningOpen}
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1.5,
            maxWidth: '380px',
            textAlign: 'center',
            border: '2px solid #ef4444',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 44, color: '#ef4444' }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
            ¿Sigues ahí?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
            Por seguridad, tu pantalla se bloqueará por inactividad en:
          </Typography>
          <Typography
            sx={{
              fontSize: '1.75rem',
              fontWeight: 900,
              fontFamily: 'monospace',
              color: '#dc2626',
              bgcolor: '#fee2e2',
              py: 0.75,
              borderRadius: '10px',
              letterSpacing: '0.05em',
            }}
          >
            {formatCountdown(secondsRemaining)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 1 }}>
          <Button
            variant="contained"
            onClick={resetTimer}
            sx={{
              bgcolor: '#0655A5',
              borderRadius: '10px',
              fontWeight: 700,
              px: 3,
              textTransform: 'none',
              '&:hover': { bgcolor: '#054487' },
            }}
          >
            Mantener sesión activa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pantalla de Bloqueo de Sesión por Inactividad (ISO 27001 - Conserva Filtros y Datos) */}
      <SessionLockModal
        open={isLocked}
        onUnlock={handleUnlock}
        onLogout={handleForceLogout}
      />
    </>
  );
}
